import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import * as api from './api';
import AttendanceRow from './AttendanceRow';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '—';
    const s = new Date(startDate + 'T00:00:00');
    const month = MONTHS[s.getMonth()];
    const dayStart = s.getDate();
    if (!endDate || endDate === startDate) return `${month} ${dayStart}`;
    const e = new Date(endDate + 'T00:00:00');
    if (s.getMonth() === e.getMonth()) return `${month} ${dayStart}–${e.getDate()}`;
    return `${month} ${dayStart} – ${MONTHS[e.getMonth()]} ${e.getDate()}`;
};

const ATTENDEE_TYPES = [
    { attendeeTypeId: 1, name: 'NPC' },
    { attendeeTypeId: 2, name: 'PC' },
    { attendeeTypeId: 3, name: 'Plot' }
];

const AttendanceManager = ({ token, event }) => {
    const [attendees, setAttendees] = useState([]);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);

    const eventDays = useMemo(() => {
        if (!event.startDate || !event.endDate) return 1;
        const start = new Date(event.startDate + 'T00:00:00');
        const end = new Date(event.endDate + 'T00:00:00');
        return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    }, [event]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [att, pl] = await Promise.all([
                api.fetchAttendance(event.eventId),
                api.fetchAllPlayers()
            ]);
            setAttendees(att);
            setPlayers(pl.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        } catch {
            toast.error('Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [event.eventId]);

    const addedPlayerIds = useMemo(
        () => new Set(attendees.map(a => a.playerId)),
        [attendees]
    );

    const handleSaveNewAttendee = async (playerId, data) => {
        try {
            await api.upsertAttendance(event.eventId, playerId, data);
            toast.success('Attendance saved');
            await loadData();
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to save attendance');
            return false;
        }
    };

    const handleUpdateAttendee = async (playerId, data) => {
        try {
            await api.upsertAttendance(event.eventId, playerId, data);
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to save attendance');
            return false;
        }
    };

    const handleDeleteAttendee = async (playerId) => {
        try {
            await api.deleteAttendance(event.eventId, playerId);
            toast.success('Attendance removed');
            await loadData();
        } catch {
            toast.error('Failed to remove attendance');
        }
    };

    if (loading) return <div className="em-loading">Loading attendance…</div>;

    return (
        <div className="em-attendance">
            <div className="em-attendance-header">
                <h2 className="em-subtitle">
                    {event.site} — {formatDateRange(event.startDate, event.endDate)}
                    <span className="em-event-type-badge">{event.eventType}</span>
                </h2>
                <div className="em-attendance-stats">
                    {attendees.length} attendee{attendees.length !== 1 ? 's' : ''}
                </div>
            </div>

            <div className="em-toolbar">
                {!showAddForm && (
                    <button className="em-btn em-btn-primary" onClick={() => setShowAddForm(true)}>
                        + Add Attendee
                    </button>
                )}
            </div>

            {showAddForm && (
                <div className="em-add-attendee-panel">
                    <AttendanceRow
                        mode="add"
                        players={players}
                        attendeeTypes={ATTENDEE_TYPES}
                        eventDays={eventDays}
                        eventStartDate={event.startDate}
                        eventId={event.eventId}
                        addedPlayerIds={addedPlayerIds}
                        onSave={handleSaveNewAttendee}
                        onCancel={() => setShowAddForm(false)}
                        token={token}
                    />
                </div>
            )}

            {attendees.length === 0 && !showAddForm ? (
                <div className="em-empty">No attendees yet. Add players to this event.</div>
            ) : (
                <div className="em-attendee-list">
                    {attendees.map(att => (
                        <AttendanceRow
                            key={att.playerId}
                            mode="edit"
                            attendee={att}
                            players={players}
                            attendeeTypes={ATTENDEE_TYPES}
                            eventDays={eventDays}
                            eventStartDate={event.startDate}
                            eventId={event.eventId}
                            addedPlayerIds={addedPlayerIds}
                            onSave={handleUpdateAttendee}
                            onDelete={() => handleDeleteAttendee(att.playerId)}
                            token={token}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AttendanceManager;
